import PptxGenJS from 'pptxgenjs'
import { ReactNode } from 'react'
import { createRoot, Root as ReactRoot } from 'react-dom/client'
import defaultProfileSrc from '@/assets/images/pptx/image-not-found.png'
import {
  PptxObjectType,
  SPECIAL_CHAR,
  SupportedPlatformList,
} from '@/utils/export-pptx/constants'
import unitConverter from '@/utils/export-pptx/unit-converter'

class RadarPptxGenerator extends PptxGenJS {
  #playground: ReactRoot
  #containerDom: HTMLDivElement
  #imageCacheMap: Record<string, string> = {}

  constructor() {
    super()
    this.#setupSlideMaster()
    this.#mountPlayground()
  }

  #setupSlideMaster = (): void => {
    SupportedPlatformList.forEach((platform) => {
      this.defineSlideMaster({
        title: platform,
        background: { color: 'ffffff' },
        objects: [
          {
            rect: {
              x: 0,
              y: 0,
              w: unitConverter.designToDefaultWidth(591),
              h: '100%',
              fill: { color: 'f8f7f7' },
            },
          },
        ],
      })
    })
  }

  #mountPlayground = (): void => {
    /**
     * 用了三層容器是為了確保添加到 body 上的元素
     * 1. 不會影響到原本 app 的 layout 佈局
     * 2. 畫布必須被 browser 渲染，而且要能被截圖，所以不能全透明，也不能 display:none，更不能被使用者看見
     * 3. 確保 pptxPlayground 的左上角的點在原點 (0,0) 上，方便後續直接使用 getBoundingClientRect() 取得元素的位置
     *   */
    this.#containerDom = document.createElement('div')
    const containerDom2 = document.createElement('div')
    const pptxDom = document.createElement('div')
    this.#containerDom.style.position = 'fixed'
    this.#containerDom.style.inset = '0'
    this.#containerDom.style.pointerEvents = 'none'
    this.#containerDom.style.zIndex = '-1'
    this.#containerDom.style.overflow = 'hidden'
    containerDom2.style.transform = 'translate(-100%,-100%)'
    containerDom2.style.overflow = 'hidden'
    pptxDom.style.transform = 'translate(100%,100%)'
    containerDom2.appendChild(pptxDom)
    this.#containerDom.appendChild(containerDom2)
    document.body.appendChild(this.#containerDom)
    this.#playground = createRoot(pptxDom)
  }

  // 比起原生的 PptxGenJS.addSlide 多了一個遍歷 #containerDom 的行為
  addSlidePromise = async (
    props?: PptxGenJS.AddSlideProps | string,
  ): Promise<PptxGenJS.Slide> => {
    const slide = super.addSlide(
      typeof props === 'string' ? { masterName: props } : props,
    )

    // 先搜集 dom 資訊
    const itemList: {
      type: PptxObjectType
      zIndex: number
      rect: DOMRect
      style: CSSStyleDeclaration
      link?: string
      src: string | null
      dom: HTMLElement
    }[] = (
      await Promise.all(
        [...this.#containerDom.querySelectorAll('[data-pptx-object]')].map(
          async (dom) => {
            if (!(dom instanceof HTMLElement)) {
              return
            }

            const pptxObjectType = dom.dataset.pptxObject as
              | PptxObjectType
              | undefined
            if (!pptxObjectType) {
              return
            }

            const zIndex = dom.dataset.zIndex ? Number(dom.dataset.zIndex) : 1

            // 如果不 decode，mac 的 powerpoint 會將超連結當成本地檔案，但如果 query string decode 了，又會變成無效的連結（因為 &），所以這邊只 decode query string 以外的部分。
            // 相關的票: https://www.notion.so/ikala/Staging-KD-Mac-PPT-c20eb696088045019125a75a08a3298a?pvs=4
            const link = ((): string | undefined => {
              if (!dom.dataset.link) {
                return
              }

              const decodedLink = decodeURIComponent(dom.dataset.link)
              if (!decodedLink.includes('&')) {
                return decodedLink
              }

              const [url, query] = decodedLink.split('?')
              return `${url}?${encodeURIComponent(query)}`
            })()

            // 為了支援近千張投影片的圖片輸出，避免 network 問題，先轉成 base64
            const src = dom.dataset.imageSrc ?? dom.getAttribute('src')
            const base64 = src && (await this.loadImage(src))

            return {
              type: pptxObjectType,
              zIndex,
              rect: dom.getBoundingClientRect(),
              style: getComputedStyle(dom),
              link,
              dom,
              src: base64,
            }
          },
        ),
      )
    )
      .filter((item) => !!item)
      // 這會先 print zIndex 最小的，再來是 zIndex 次小的，以此類推，來模擬 css z-index 的效果
      .toSorted((a, b) => a.zIndex - b.zIndex)

    itemList.forEach((layoutInfo) => {
      const { type, dom, rect, style, link, src } = layoutInfo

      switch (type) {
        case PptxObjectType.Text: {
          if (!dom.innerText) {
            return
          }

          const textInfo: PptxGenJS.TextPropsOptions = {
            ...textConfigDefault,
            x: unitConverter.designToDefaultWidth(rect.x),
            y: unitConverter.designToDefaultHeight(rect.y),
            w: unitConverter.designToDefaultWidth(rect.width + 2), // 增加一些容忍的空間，避免 px inch 四捨五入誤差導致的換行
            h: unitConverter.designToDefaultHeight(rect.height),
            fontSize: unitConverter.designToDefaultFontSize(
              Number(style.fontSize.split('px')[0]),
            ),
            color: cssColorStringToHex(style.color),
            bold: Number(style.fontWeight) >= 500,
            charSpacing:
              style.letterSpacing === 'normal'
                ? undefined
                : unitConverter.designToDefaultFontSize(
                    Number(style.letterSpacing.split('px')[0]),
                  ),
            lineSpacing: unitConverter.designToDefaultFontSize(
              Number(style.lineHeight.split('px')[0]),
            ),
            align: style.textAlign === 'center' ? 'center' : undefined,
          }

          if (!!link) {
            textInfo['hyperlink'] = { url: link }
          }

          const innerText = dom.innerText
          const text = dom.dataset.ellipsis
            ? ellipsisString(innerText)
            : innerText
          // pptxgenjs 在產生文字時，如果文字中有特殊字元就會產生一個無法開啟的檔案，所以我們在這邊過濾掉特殊字元
          slide.addText(text.replaceAll(SPECIAL_CHAR, ''), textInfo)
          break
        }
        case PptxObjectType.Image: {
          if (!src) {
            return
          }

          const size = {
            w: unitConverter.designToDefaultWidth(rect.width),
            h: unitConverter.designToDefaultHeight(rect.height),
          }

          const imageInfo: PptxGenJS.ImageProps = {
            data: src,
            x: unitConverter.designToDefaultWidth(rect.x),
            y: unitConverter.designToDefaultHeight(rect.y),
            rounding: style.borderRadius === '50%',
            ...(dom.hasAttribute('data-original-size')
              ? size
              : {
                  sizing: {
                    type: 'cover',
                    ...size,
                  },
                }),
          } as const

          if (!!link) {
            imageInfo['hyperlink'] = { url: link }
          }
          slide.addImage(imageInfo)
          break
        }
        case PptxObjectType.Shape: {
          const type = style.borderRadius === '0px' ? 'rect' : 'roundRect'
          const hasBorder = style.borderWidth !== '0px'
          const shapeInfo: PptxGenJS.ShapeProps = {
            fill: { color: cssColorStringToHex(style.backgroundColor) },
            rectRadius: 0.03,
            line: hasBorder
              ? {
                  color: cssColorStringToHex(style.borderColor),
                  width: unitConverter.designToDefaultWidth(
                    Number(style.borderWidth.split('px')[0]),
                  ),
                }
              : undefined,
            x: unitConverter.designToDefaultWidth(rect.x),
            y: unitConverter.designToDefaultHeight(rect.y),
            w: unitConverter.designToDefaultWidth(rect.width),
            h: unitConverter.designToDefaultHeight(rect.height),
          } as const

          slide.addShape(type, shapeInfo)
          break
        }
        default: {
          const theType: never = type
          throw new Error(`pptxObject type ${theType} is not supported`)
        }
      }
    })

    return slide
  }

  // 為了支援近千張投影片的圖片輸出，避免 network 問題，先轉成 base64
  loadImage = async (url: string): Promise<string> => {
    if (this.#imageCacheMap[url]) {
      return this.#imageCacheMap[url]
    }

    const base64 = await new Promise<string>((resolve) => {
      getImageBase64(url)
        .then(resolve)
        .catch(() => getImageBase64(defaultProfileSrc).then(resolve))
    })

    this.#imageCacheMap[url] = base64
    return base64
  }

  render = (children: ReactNode): void => this.#playground.render(children)

  generate = async (filename: string): Promise<string> => {
    const result = await super.writeFile({
      fileName: `${filename}.pptx`,
    })
    return result
  }

  destroy = (): void => {
    document.body.removeChild(this.#containerDom)
    this.#playground.unmount()
    this.#imageCacheMap = {}
  }
}

export default RadarPptxGenerator

const textConfigDefault = {
  fontFace: 'Arial',
  align: 'left',
  valign: 'top',
  inset: 0,
} as const

async function getImageBase64(url: string): Promise<string> {
  try {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Failed to fetch image ${url}`)
    }

    const blob = await response.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = (): void => {
        resolve(reader.result as string)
      }
      reader.onerror = (e): void => {
        reject(e)
      }
      reader.readAsDataURL(blob)
    })
  } catch (e) {
    throw new Error(`Failed to fetch image: ${e.message}`)
  }
}

function componentToHex(c: number): string {
  const hex = c.toString(16)
  return hex.length === 1 ? `0${hex}` : hex
}

function rgbToHex(r: string, g: string, b: string): string {
  return `${componentToHex(Number(r))}${componentToHex(
    Number(g),
  )}${componentToHex(Number(b))}`
}

function cssColorStringToHex(cssColorString: string): string {
  const match = cssColorString.match(/\((\d+), (\d+), (\d+)\)/)
  const [, r, g, b] = match ?? ['0', '0', '0', '0']
  return rgbToHex(r, g, b)
}

function ellipsisString(str: string): string {
  let count = 0
  let result = ''
  let isEllipsis = false

  for (const char of str) {
    // 根據是否為全形字符來增加計數
    const isFullWidth = /[\uFF00-\uFFEF\u4E00-\u9FAF]/.test(char)
    count += isFullWidth ? 2 : 1

    if (count <= 50) {
      result += char
    } else {
      isEllipsis = true
      break
    }
  }

  if (isEllipsis) {
    result += '...'
  }

  return result
}
